'use client'

import * as React from 'react'
import { reportError } from '@/lib/errors/report'
import { ErrorFallback } from '@/components/errors/errorFallback'
import type { ErrorTier } from '@/lib/errors/messages'

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode | ((ctx: { error: unknown; reset: () => void }) => React.ReactNode)
  onReset?: () => void
  onError?: (error: unknown, info: React.ErrorInfo) => void
  /** When any of these values change, the boundary auto-recovers. */
  resetKeys?: unknown[]
  /** Used in default fallback copy and telemetry, e.g. "the allocation chart". */
  label?: string
  tier?: ErrorTier
  backHref?: string
}

interface ErrorBoundaryState {
  error: unknown
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: unknown) {
    return { error }
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    reportError(error, { label: this.props.label, componentStack: info.componentStack })
    this.props.onError?.(error, info)
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (!this.state.error || !this.props.resetKeys) return
    const changed = this.props.resetKeys.some((key, i) => key !== prevProps.resetKeys?.[i])
    if (changed) this.reset()
  }

  reset = () => {
    this.props.onReset?.()
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    if (typeof this.props.fallback === 'function') {
      return this.props.fallback({ error, reset: this.reset })
    }
    if (this.props.fallback) return this.props.fallback

    return (
      <ErrorFallback
        tier={this.props.tier ?? 'component'}
        error={error}
        reset={this.reset}
        onBack={() => {
          if (this.props.backHref && typeof window !== 'undefined') {
            window.location.assign(this.props.backHref)
          }
        }}
        title={this.props.label ? `We couldn't load ${this.props.label}` : undefined}
        compact
      />
    )
  }
}
